import {
  Banner,
  Box,
  FlexBox,
  Modal,
  ModalBody,
  ModalFoot,
  ModalHead,
  Text,
} from '@dixa/lib-shared-habla';
import classnames from 'classnames';
import { ampli } from 'lib-shared-tracking';
import type { SearchUser } from 'lib-shared-types-api';
import {
  ComponentProps,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useLocation } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';

import { companiesApi } from 'apis/companies';
import { contactsApi } from 'apis/contacts';
import { usersApi } from 'apis/users';
import { actions as conversationActions } from 'components/conversation/actions/conversation';
import { actions as conversationsActions } from 'components/conversation/actions/conversations';
import { notificationActions } from 'components/notifications/actions/notification';
import { NotificationType, useNotifications } from 'hooks/useNotifications';
import { createAppModal } from 'utils/modal';
import { pubSub } from 'utils/pubSub';
import { useAppDispatch } from 'utils/redux/useAppDispatch';

import { CompareContacts } from './components/compareContacts/compareContacts';
import { PreviewContact } from './components/previewContact/previewContact';
import { SelectContact } from './components/selectContact/selectContact';
import styles from './mergeContactsModal.module.scss';
import { Contact, DryRunMergeError } from './mergeContactsModal.types';
import { getMappedMergeDryRunErrors } from './utils/mapMergeDryRunErrors';

export type MergeContactsStep = {
  /** Title to show in modal title in step */
  title: string;
  /** Children to show in ModalBody */
  children: ReactNode;
  /** Props for Modal foot in step */
  modalFootProps: ComponentProps<typeof ModalFoot>;
};

/** A component that displays a modal for merging contacts (end users) */
export const MergeContactsModal = createAppModal(
  { type: 'app:mergeContacts' },
  ({ isOpen, modal, hide }): JSX.Element => {
    const dispatch = useAppDispatch();
    const location = useLocation();
    const [mergeContacts, { isLoading: isMergingContacts }] =
      contactsApi.useMergeContactsMutation();
    const [getUser, { isLoading: isLoadingUser }] =
      usersApi.useLazyReadUserQuery();
    const [getCompany] = companiesApi.useLazyReadCompanyQuery();
    const notify = useNotifications();
    const [targetContact, setTargetContact] = useState<Contact>();
    const [sourceContact, setSourceContact] = useState<Contact>();
    const [previewContact, setPreviewContact] = useState<Contact>();
    const [errorMessages, setErrorMessages] = useState<
      { title?: string; description: string }[]
    >([]);
    const [currentStep, setCurrentStep] = useState(0);
    const { user: modalArgUser } = modal.arg;

    useEffect(() => {
      if (!modalArgUser.company_id) {
        setTargetContact(modalArgUser);
        return;
      }

      (async () => {
        if (!modalArgUser.company_id) return;

        try {
          const { data } = await getCompany(
            {
              id: modalArgUser.company_id,
            },
            true,
          ).unwrap();

          setTargetContact({ ...modalArgUser, company: data });
        } catch (error) {
          setErrorMessages(
            (
              error as unknown as { data: { errors: string[] } }
            ).data.errors.map((unknownError) => ({
              description: unknownError,
            })),
          );
        }
      })();
    }, [getCompany, modalArgUser]);

    useEffect(() => {
      if (isOpen) {
        ampli.mergeContactsModalOpened();
      }

      return () => {
        ampli.mergeContactsModalClosed();
      };
    }, [isOpen]);

    const handleCancel = useCallback(() => {
      hide?.();

      if (sourceContact) {
        ampli.mergeContactsMergeCancelled();
      }
    }, [hide, sourceContact]);

    const getAndSetSourceContact = useCallback(
      async (contact: SearchUser) => {
        const results = await Promise.allSettled([
          getUser({ id: contact.id }, true).unwrap(),
          ...[
            contact.company_id
              ? getCompany(
                  {
                    id: contact.company_id,
                  },
                  true,
                ).unwrap()
              : null,
          ],
        ]);

        /** If the getUser fails */
        if (results[0].status === 'rejected') {
          throw results[0].reason;
        }

        /** If the getCompany fails */
        if (results[1].status === 'rejected') {
          throw results[1].reason;
        }

        setSourceContact({
          ...results[0].value.data,
          company: results[1].value?.data,
        });
      },
      [getCompany, getUser],
    );

    const dryMergeAndSetPreviewContact = useCallback(
      async (contact: SearchUser) => {
        if (!targetContact) return;

        const { data: mergedContact } = await mergeContacts({
          target: targetContact.id,
          dryRun: true,
          sources: [contact.id],
          strategy: 'AutoOverlapping',
        }).unwrap();

        if (mergedContact.company_id) {
          const { data: companyData } = await getCompany(
            {
              id: mergedContact.company_id,
            },
            true,
          ).unwrap();

          setPreviewContact({
            ...mergedContact,
            company: companyData,
          });
        } else {
          setPreviewContact(mergedContact);
        }
      },
      [getCompany, mergeContacts, targetContact],
    );

    const handleOnContactChange = useCallback(
      async (contact: SearchUser) => {
        if (!targetContact) return;

        setErrorMessages([]);
        setSourceContact(undefined);
        setPreviewContact(undefined);

        ampli.mergeContactsSourceContactSelected();

        try {
          const results = await Promise.allSettled([
            getAndSetSourceContact(contact),
            dryMergeAndSetPreviewContact(contact),
          ]);

          results.forEach((result) => {
            if (result.status === 'rejected') {
              throw result.reason;
            }
          });
        } catch (error) {
          const { data } = error as unknown as {
            data: {
              title: string;
              causes: DryRunMergeError[];
              errors: string[];
            };
          };

          const errorMessages: { title?: string; description: string }[] = [];

          if (!data) {
            errorMessages.push({ description: JSON.stringify(error) });
          }

          if (data && !data.causes?.length) {
            errorMessages.push({ description: data.title });
          }

          if (data.causes?.length) {
            const errors = getMappedMergeDryRunErrors(data.causes, [
              targetContact,
              contact,
            ]).map((dryRunMergeError) => ({
              title: dryRunMergeError.title,
              description: dryRunMergeError.detail,
            }));

            errorMessages.push(...errors);
          }

          if (data?.errors?.length) {
            const errors = data.errors.map((errorMessage) => ({
              description: errorMessage,
            }));

            errorMessages.push(...errors);
          }

          setErrorMessages(errorMessages);
        }
      },
      [dryMergeAndSetPreviewContact, getAndSetSourceContact, targetContact],
    );

    const handleMerge = useCallback(async () => {
      if (!sourceContact || !targetContact) return;

      try {
        const { data: mergedContact } = await mergeContacts({
          target: targetContact.id,
          sources: [sourceContact.id],
          strategy: 'AutoOverlapping',
          dryRun: false,
        }).unwrap();

        const notificationID = uuidv4();
        const targetContactRoute = `/contacts/user/${targetContact?.id}`;
        const isTargetContactRoute = location.pathname === targetContactRoute;

        notify(
          {
            id: notificationID,
            title: 'Contact merged!',
            text: `Your contacts are now merged. ${
              !isTargetContactRoute
                ? 'If you want to further edit the new contact, click "go to contact".'
                : ''
            }`,
            className: 'success',
            icon: 'i-done',
            time: 5000,
            ...(!isTargetContactRoute && {
              leftAction: {
                action: {
                  navigate: `/contacts/user/${targetContact?.id}`,
                },
                text: 'Go to contact',
              },
            }),
            rightAction: {
              text: 'Dismiss',
              action: () => {
                dispatch(
                  notificationActions.notificationRemoveById({
                    id: notificationID,
                  }),
                );
              },
            },
          },
          NotificationType.Success,
        );

        handleCancel();
        const numberOfEmails =
          (mergedContact.email ? 1 : 0) +
          (mergedContact.additional_emails?.length ?? 0);
        const numberOfPhoneNumbers =
          (mergedContact.phone_number ? 1 : 0) +
          (mergedContact.additional_phone_numbers?.length ?? 0);
        ampli.mergeContactsMergeSucceeded({
          numberOfEmails,
          numberOfPhoneNumbers,
        });
        pubSub.publish('mergeContactsModalUserMerged', mergedContact);
        dispatch(
          conversationsActions.conversationsUpdateUser({
            data: mergedContact,
          }),
        );
        dispatch(
          conversationActions.conversationUpdateUser({
            data: mergedContact,
          }),
        );
      } catch (error) {
        const { data } = error as unknown as {
          data: { title: string };
        };

        notify(
          {
            title: 'Merging failed!',
            text: `${data.title} If the problem persists, reach out to support.`,
            className: 'danger',
            icon: 'i-exclamation',
            time: 20000,
          },
          NotificationType.Error,
        );

        handleCancel();
        ampli.mergeContactsMergeFailed();
      }
    }, [
      dispatch,
      handleCancel,
      location.pathname,
      mergeContacts,
      notify,
      sourceContact,
      targetContact,
    ]);

    const mergeContactsSteps = useMemo((): MergeContactsStep[] => {
      return [
        {
          title: 'Merge Contacts',
          children: targetContact && (
            <SelectContact
              targetContact={targetContact}
              onContactChange={handleOnContactChange}
            />
          ),
          modalFootProps: {
            confirmName: 'Next',
            confirmOnClick: () => setCurrentStep((value) => value + 1),
            confirmProps: {
              disabled:
                !!errorMessages.length || isLoadingUser || !sourceContact,
            },
            cancelName: 'Cancel',
            cancelOnClick: handleCancel,
          },
        },
        {
          title: 'Merge Contacts',
          children: targetContact && sourceContact && previewContact && (
            <CompareContacts
              targetContact={targetContact}
              sourceContact={sourceContact}
              previewContact={previewContact}
            />
          ),
          modalFootProps: {
            confirmName: 'Next',
            confirmOnClick: () => setCurrentStep((value) => value + 1),
            cancelName: 'Back',
            cancelOnClick: () => setCurrentStep((value) => value - 1),
          },
        },
        {
          title: 'Merge Contacts',
          children: targetContact && sourceContact && previewContact && (
            <PreviewContact
              targetContact={targetContact}
              sourceContact={sourceContact}
              previewContact={previewContact}
            />
          ),
          modalFootProps: {
            confirmName: 'Merge',
            confirmOnClick: handleMerge,
            cancelName: 'Back',
            cancelOnClick: () => setCurrentStep((value) => value - 1),
            confirmProps: {
              isLoading: isMergingContacts,
            },
          },
        },
      ];
    }, [
      targetContact,
      handleOnContactChange,
      errorMessages.length,
      isLoadingUser,
      sourceContact,
      handleCancel,
      previewContact,
      handleMerge,
      isMergingContacts,
    ]);

    return (
      <Modal
        isOpen={isOpen}
        onClose={handleCancel}
        className={classnames(styles.modal, {
          [styles.modalEnlarged]: currentStep !== 0,
        })}
        data-testid="merge-contacts-modal"
      >
        <ModalHead
          title={mergeContactsSteps[currentStep].title}
          onClose={handleCancel}
        />
        <ModalBody>
          {targetContact &&
            mergeContactsSteps.map((step, index) => (
              <Box
                key={`step-${index}`}
                style={{ display: index === currentStep ? 'block' : 'none' }}
              >
                {step.children}
              </Box>
            ))}

          {!!errorMessages.length && (
            <FlexBox gap="200" flexDirection="column">
              {errorMessages.map((error, index) => (
                <Banner
                  key={`error-${index}`}
                  variant="fail"
                  headline={error.title}
                  description={
                    <Text.Paragraph>{error.description}</Text.Paragraph>
                  }
                />
              ))}
            </FlexBox>
          )}
        </ModalBody>
        <ModalFoot {...mergeContactsSteps[currentStep].modalFootProps} />
      </Modal>
    );
  },
);
